/**
 * Recipe Library - Educational recipe ideas
 * commonly discussed in integrative and macrobiotic dietary approaches
 */

export const recipes = [
  // BREAKFAST
  {
    id: 'brown-rice-porridge',
    name: 'Brown Rice Porridge with Cooked Apples',
    type: 'Breakfast',
    whyChosen: 'Often included in macrobiotic and whole-grain dietary patterns. Uses gentle cooking methods common in traditional approaches.',
    ingredients: [
      'Cooked brown rice',
      'Water or unsweetened plant milk',
      'Apple, diced',
      'Cinnamon (optional)',
      'Pinch of sea salt'
    ],
    preparation: [
      'Add cooked brown rice to a pot with water or plant milk (1:2 ratio)',
      'Bring to a gentle simmer over medium-low heat',
      'Add diced apple and a pinch of salt',
      'Cook for 10-15 minutes, stirring occasionally, until creamy',
      'Add cinnamon if desired',
      'Serve warm'
    ],
    preparationNotes: 'Can be made with leftover rice. Some people prefer a thinner consistency. Often eaten warm in the morning.',
    relatedFoods: ['brown-rice']
  },
  {
    id: 'millet-porridge',
    name: 'Millet Porridge with Cinnamon',
    type: 'Breakfast',
    whyChosen: 'Common in macrobiotic dietary patterns. Often discussed as a gentle, alkaline-forming grain.',
    ingredients: [
      'Millet',
      'Water',
      'Cinnamon',
      'Pinch of sea salt',
      'Optional: raisins or dates'
    ],
    preparation: [
      'Rinse millet thoroughly',
      'Combine 1 part millet with 3-4 parts water in a pot',
      'Add a pinch of salt',
      'Bring to a boil, then reduce heat to low',
      'Simmer covered for 20-25 minutes until creamy',
      'Stir in cinnamon',
      'Serve warm with optional dried fruit'
    ],
    preparationNotes: 'Can be made creamier by adding more water. Some people toast millet before cooking for enhanced flavor.',
    relatedFoods: ['millet']
  },
  {
    id: 'buckwheat-porridge',
    name: 'Buckwheat Porridge with Berries',
    type: 'Breakfast',
    whyChosen: 'Uses whole buckwheat groats, common in traditional dietary patterns. Often discussed as a warming breakfast option.',
    ingredients: [
      'Buckwheat groats',
      'Water',
      'Fresh or frozen berries',
      'Pinch of sea salt',
      'Optional: cinnamon or vanilla'
    ],
    preparation: [
      'Rinse buckwheat groats',
      'Combine 1 part buckwheat with 2-3 parts water',
      'Add a pinch of salt',
      'Bring to a boil, then reduce to simmer',
      'Cook covered for 15-20 minutes until tender',
      'Top with berries',
      'Add cinnamon or vanilla if desired'
    ],
    preparationNotes: 'Buckwheat cooks quickly. Some people prefer to soak it overnight first.',
    relatedFoods: ['buckwheat']
  },

  // LUNCH
  {
    id: 'miso-soup',
    name: 'Miso Soup with Tofu and Greens',
    type: 'Lunch',
    whyChosen: 'Central to macrobiotic dietary tradition. Uses fermented foods and leafy greens common in traditional Japanese dietary patterns.',
    ingredients: [
      'Water',
      'Wakame seaweed (small piece)',
      'Tofu, cubed',
      'Leafy greens (spinach, kale, or watercress)',
      'Miso paste (1-2 tablespoons per bowl)',
      'Green onions, sliced (optional)'
    ],
    preparation: [
      'Soak wakame in warm water for 5 minutes, then chop',
      'Bring water to a gentle simmer',
      'Add wakame and simmer for 5 minutes',
      'Add tofu cubes and greens',
      'Cook until greens are just wilted',
      'Remove from heat',
      'Dissolve miso paste in a small amount of broth, then stir into soup',
      'Garnish with green onions if desired'
    ],
    preparationNotes: 'Do not boil after adding miso. Often consumed at the start of a meal in macrobiotic practice.',
    relatedFoods: ['miso', 'seaweed', 'spinach', 'kale', 'watercress']
  },
  {
    id: 'lentil-vegetable-soup',
    name: 'Lentil & Vegetable Soup',
    type: 'Lunch',
    whyChosen: 'Often included in plant-forward eating patterns. Uses legumes and vegetables common in whole-food dietary approaches.',
    ingredients: [
      'Lentils (red or green)',
      'Carrots, diced',
      'Onion, diced',
      'Celery, diced (optional)',
      'Vegetable broth or water',
      'Sea salt and herbs (thyme, bay leaf)',
      'Leafy greens (optional)'
    ],
    preparation: [
      'Rinse lentils',
      'Sauté onion and carrots in a pot with a small amount of water',
      'Add lentils and broth/water',
      'Add herbs and salt',
      'Bring to a boil, then simmer covered for 25-30 minutes',
      'Add leafy greens in last 5 minutes if using',
      'Adjust seasoning and serve'
    ],
    preparationNotes: 'Red lentils cook faster and become creamy. Can be made in advance and reheated.',
    relatedFoods: ['lentils', 'carrots', 'spinach']
  },
  {
    id: 'chickpea-bowl',
    name: 'Chickpea & Steamed Vegetable Bowl',
    type: 'Lunch',
    whyChosen: 'Uses legumes and steamed vegetables, common in Mediterranean and whole-food dietary patterns.',
    ingredients: [
      'Cooked chickpeas',
      'Broccoli florets',
      'Carrots, sliced',
      'Brown rice or millet',
      'Tahini or sesame dressing',
      'Lemon juice',
      'Sea salt and herbs'
    ],
    preparation: [
      'Cook grain according to package directions',
      'Steam broccoli and carrots until tender',
      'Warm chickpeas gently',
      'Arrange grain, vegetables, and chickpeas in a bowl',
      'Mix tahini with lemon juice and a small amount of water to make dressing',
      'Drizzle dressing over bowl',
      'Season with salt and herbs'
    ],
    preparationNotes: 'Can be served warm or at room temperature. Components can be prepared in advance.',
    relatedFoods: ['chickpeas', 'broccoli', 'carrots', 'brown-rice', 'millet', 'sesame-seeds']
  },

  // DINNER
  {
    id: 'steamed-fish-bowl',
    name: 'Steamed Fish with Greens and Brown Rice',
    type: 'Dinner',
    whyChosen: 'Uses gentle cooking methods common in macrobiotic and traditional dietary patterns. Combines protein with whole grains and greens.',
    ingredients: [
      'White fish (cod, halibut, or flounder)',
      'Brown rice, cooked',
      'Leafy greens (kale or spinach)',
      'Lemon',
      'Ginger, sliced (optional)',
      'Sea salt',
      'Sesame seeds (optional)'
    ],
    preparation: [
      'Season fish lightly with salt',
      'Place fish in a steamer basket',
      'Add ginger slices if using',
      'Steam for 8-12 minutes until fish flakes easily',
      'Steam greens separately until just wilted',
      'Serve fish over brown rice with greens',
      'Squeeze lemon over fish',
      'Sprinkle with sesame seeds if desired'
    ],
    preparationNotes: 'Cooking time varies by fish thickness. Some people prefer to steam all components together.',
    relatedFoods: ['brown-rice', 'kale', 'spinach', 'sesame-seeds']
  },
  {
    id: 'tofu-stir-fry',
    name: 'Stir-Fried Tofu with Broccoli and Carrots',
    type: 'Dinner',
    whyChosen: 'Uses vegetables common in plant-forward dietary patterns. Often included in Asian-inspired macrobiotic cooking.',
    ingredients: [
      'Firm tofu, cubed and pressed',
      'Broccoli florets',
      'Carrots, sliced',
      'Ginger, minced',
      'Garlic, minced (optional)',
      'Sesame oil (small amount)',
      'Tamari or soy sauce',
      'Brown rice or millet for serving'
    ],
    preparation: [
      'Press tofu to remove excess water',
      'Heat a small amount of sesame oil in a pan',
      'Add tofu and cook until lightly browned',
      'Remove tofu and set aside',
      'Add ginger and garlic, stir briefly',
      'Add vegetables with a splash of water',
      'Cover and steam until tender-crisp',
      'Return tofu to pan, add tamari',
      'Serve over grain'
    ],
    preparationNotes: 'Can add water instead of oil for oil-free cooking. Vegetables should remain slightly crisp.',
    relatedFoods: ['broccoli', 'carrots', 'sesame-seeds', 'brown-rice', 'millet']
  },
  {
    id: 'sweet-potato-tahini',
    name: 'Sweet Potato with Tahini Dressing',
    type: 'Dinner',
    whyChosen: 'Simple preparation common in whole-food dietary patterns. Combines naturally sweet vegetables with sesame.',
    ingredients: [
      'Sweet potato',
      'Tahini',
      'Lemon juice',
      'Water',
      'Sea salt',
      'Optional: leafy greens, chickpeas'
    ],
    preparation: [
      'Wash sweet potato and pierce with fork',
      'Bake at 200°C (400°F) for 45-60 minutes until soft',
      'Or steam chunks for 20-25 minutes',
      'Mix tahini with lemon juice and water until smooth',
      'Add salt to taste',
      'Cut sweet potato open and drizzle with tahini dressing',
      'Serve with greens or chickpeas if desired'
    ],
    preparationNotes: 'Can be prepared in advance and reheated. Some people prefer steaming over baking for softer texture.',
    relatedFoods: ['sweet-potato', 'sesame-seeds', 'chickpeas']
  },

  // SNACKS / SIDES
  {
    id: 'steamed-kale-sesame',
    name: 'Steamed Kale with Sesame',
    type: 'Snack',
    whyChosen: 'Simple preparation common in macrobiotic dietary patterns. Uses leafy greens and sesame seeds.',
    ingredients: [
      'Kale',
      'Sesame seeds',
      'Tamari or sea salt',
      'Lemon juice (optional)'
    ],
    preparation: [
      'Wash kale and remove thick stems',
      'Chop into bite-size pieces',
      'Steam for 3-5 minutes until just tender',
      'Toast sesame seeds lightly in a dry pan',
      'Toss kale with sesame seeds',
      'Season with tamari or salt',
      'Add lemon juice if desired'
    ],
    preparationNotes: 'Can be served warm or at room temperature. Often used as a side dish or snack.',
    relatedFoods: ['kale', 'sesame-seeds']
  },
  {
    id: 'sauerkraut-rice',
    name: 'Sauerkraut with Brown Rice',
    type: 'Snack',
    whyChosen: 'Uses fermented foods common in traditional dietary patterns. Often served as a small side dish.',
    ingredients: [
      'Unpasteurized sauerkraut',
      'Cooked brown rice',
      'Optional: caraway seeds'
    ],
    preparation: [
      'Cook brown rice according to package directions',
      'Serve rice in a bowl',
      'Top with a small amount of sauerkraut (1-2 tablespoons)',
      'Mix together or eat separately',
      'Add caraway seeds if desired'
    ],
    preparationNotes: 'Start with small amounts of sauerkraut. Some people find it easier to digest when warmed slightly (do not heat above body temperature to preserve probiotics).',
    relatedFoods: ['sauerkraut', 'brown-rice']
  },
  {
    id: 'roasted-pumpkin-seeds',
    name: 'Roasted Pumpkin Seeds',
    type: 'Snack',
    whyChosen: 'Simple preparation of seeds common in whole-food dietary patterns.',
    ingredients: [
      'Raw pumpkin seeds',
      'Sea salt (optional)',
      'Tamari (optional)'
    ],
    preparation: [
      'Spread pumpkin seeds on a baking sheet',
      'Roast at 160°C (325°F) for 10-15 minutes',
      'Stir occasionally until lightly golden',
      'Season with salt or tamari if desired',
      'Cool before storing'
    ],
    preparationNotes: 'Can be stored in an airtight container. Some people prefer to soak seeds overnight before roasting.',
    relatedFoods: ['pumpkin-seeds']
  },

  // BEVERAGES
  {
    id: 'green-tea',
    name: 'Green Tea (Sencha / Matcha)',
    type: 'Beverage',
    whyChosen: 'Common in Asian dietary traditions and often discussed in integrative approaches.',
    ingredients: [
      'Green tea leaves (sencha) or matcha powder',
      'Hot water (not boiling)'
    ],
    preparation: [
      'Heat water to 70-80°C (160-175°F)',
      'For sencha: steep 1 teaspoon leaves for 1-2 minutes',
      'For matcha: whisk 1/2 teaspoon powder with small amount of hot water',
      'Strain sencha or dilute matcha with more water',
      'Serve immediately'
    ],
    preparationNotes: 'Avoid boiling water which can create bitterness. Some people avoid drinking on an empty stomach.',
    relatedFoods: ['green-tea']
  },
  {
    id: 'turmeric-tea',
    name: 'Turmeric Tea',
    type: 'Beverage',
    whyChosen: 'Traditional in Ayurvedic dietary patterns. Often discussed in integrative nutrition.',
    ingredients: [
      'Fresh turmeric root or turmeric powder',
      'Hot water',
      'Black pepper (small pinch)',
      'Optional: ginger, lemon, honey'
    ],
    preparation: [
      'Slice fresh turmeric or use 1/2 teaspoon powder',
      'Add to hot water',
      'Add a small pinch of black pepper',
      'Steep for 5-10 minutes',
      'Add ginger or lemon if desired',
      'Strain if using fresh turmeric'
    ],
    preparationNotes: 'Black pepper may help absorption. Some people prefer to make this as a warm beverage rather than hot tea.',
    relatedFoods: ['turmeric-tea']
  }
];

export const recipeTypes = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Snack',
  'Beverage'
];
